import { put, call, select, takeLatest } from 'redux-saga/effects';
import { updateUserInfo } from '../../actions/auth';
import { userInfoSelector } from '../../selectors/auth';

// constants
import {
  EDIT_MACHINE,
  LIST_MACHINE,
  EDIT_GAME,
  LIST_GAME,
  EDIT_ASSIGN_MACHINE,
  LIST_ASSIGN_MACHINE,
  UPDATE_READING,
  LIST_READING,
  LIST_MATCH_TICKETS,
  CLOCK_IN,
  CLOCK_OUT,
  LOAD_CLOCK_RECORD,
  LIST_EMPLOYEE_SESSION,
  UPDATE_EMPLOYEE_STATUS,
  MANUAL_CLOCK_IN,
  DELETE_EMPLOYEE_SESSION,
  FORCE_CLOCK_OUT,
  GET_POINT_WATCHING,
  GET_POINT_WATCHING_BY_DATE,
  GET_POINT_WATCHING_EMPLOYEE_DAYS,
  GET_POINT_WATCHING_DAY_ENTRIES,
  GET_POINT_WATCHING_ALERTS,
  CLEAR_POINT_WATCHING_ALERT,
  MATCH_POINT_PDF_PREVIEW,
  MASTER_READING_PDF_PREVIEW,
  LIST_SESSION_IDS,
  ADD_MACHINE_PICTURE,
  LIST_MACHINE_PICTURE,
  DELETE_MACHINE_PICTURE,
  EDIT_CATEGORY,
  LIST_CATEGORY,
  AVAILABLE_EMPLOYEE_SESSIONS,
  START_SESSION,
  END_SESSION,
  LOAD_STARTED_SESSION,
  RESET_ASSIGN_MACHINE,
  LIST_NOTES,
  ADD_NOTE,
  DELETE_NOTE,
  LIST_REPORT,
  LIST_SHORT_ANALYSIS,
  UPDATE_REPORT_FIELD,
  LIST_COMPANIES,
  LIST_AVAILABLE_COMPANIES,
  CREATE_COMPANY,
  UPDATE_COMPANY,
  GENERATE_LICENSE,
  ACTIVATE_LICENSE,
  GET_LICENSE,
  RENEW_LICENSE,
  REVOKE_LICENSE,
  LIST_ACTIVE_LICENSES,
  UPDATE_LICENSE_AMOUNT,
  LIST_LICENSE_PAYMENTS,
  UPDATE_LICENSE_PAYMENT,
  RECORD_NEXT_CYCLE_PAYMENT,
  MARK_CYCLE_PAID,
  GET_COMPANY_MEMBERSHIPS,
  YEARLY_PROJECTIONS,
  ACTUAL_PROJECTIONS,
  GET_PREV_IN_OUT,
  UPDATE_MANAGER_BANK,
  GET_MANAGER_SETTING,
  SAVE_MANAGER_SETTING,
  SAVE_MANAGER_SUMMARY,
  LIST_MASTER_ADMINS_LOCATION,
  OPEN_SESSION_READING,
  GET_LAST_SESSION_READING,
  CANCEL_SESSION,
  LIST_DELETED_READING_SESSIONS,
  RESTORE_READING_SESSION,
  DELETE_FOREVER_READING_SESSION,
  GRANT_MAINTENANCE_PERMIT,
  REVOKE_MAINTENANCE_PERMIT,
  LOAD_MAINTENANCE_PERMIT,
} from '../../constants/settings';
import {
  MACHINE,
  GAME,
  CLOCK,
  CUSTOMER,
  DASHBOARD,
  BONUS_API,
  RULES,
  TRANSACTION,
  NOTE,
  REPORT,
  COMPANY,
  MAINTENANCE_PERMIT,
} from '../../../components/global/Server/endpoints';

// secondary actions
import {
  storeMachine,
  storeGame,
  storeAssignMachine,
  storeReading,
  storeAllReadings,
  storeAllMatchTickets,
  storeClock,
  storeAllEmployeeSession,
  storeUpdatedEmployeeSession,
  storePointWatching,
  storePointWatchingByDate,
  storePointWatchingEmployeeDays,
  storePointWatchingDayEntries,
  storePointWatchingAlerts,
  storeMatchPointPDFPreviewReady,
  storeMasterReadingPDFPreviewReady,
  storeClaimBonus,
  storeUpdatedBonusImageWithPreview,
  storeManageBonus,
  storeRules,
  storeAllSessionIds,
  storeAllMachinePicture,
  startLoading,
  stopLoading,
  storeCategory,
  storeAvailableEmployeeSessions,
  storeStartedSession,
  storeNotes,
  storeReport,
  storeShortAnalysis,
  storeCompanies,
  storeAvailableCompanies,
  storeCompanyMemberships,
  storeLicenseKey,
  storeLicensePayments,
  storeActiveLicense,
  storeYearlyProjections,
  storeActualProjections,
  storeManagerSetting,
  storeMasterAdmins,
  listClaimBonus,
  storeMaintenancePermit,
  storeMaintenancePermitHistory,
} from '../../actions/settings';

import {
  showErrorMessage,
  hideError,
  requestStatus,
  clearStatus,
} from '../../actions/global';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import { HTTP_SUCCESS } from '../../constants/global';
import {
  DELETE_BONUS,
  DELETE_MANAGE_BONUS,
  LIST_CLAIM_BONUS,
  LIST_MANAGE_BONUS,
  LIST_RULES,
  PREVIEW_BONUS_IMAGE,
  SAVE_BONUS,
  SAVE_MANAGE_BONUS,
  SAVE_RULES,
  UPLOAD_BONUS_PHOTO,
} from '../../constants/ticketOut';
import AuthUtil from '../../AuthUtil';

// companies sagas
function* listCompaniesSaga() {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.getRequest(COMPANY.LIST_COMPANIES)
    );
    yield put(storeCompanies(response.data));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listMasterAdminPerLocationSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.LIST_MASTER_ADMIN, action.data)
    );
    yield put(storeMasterAdmins(response.data.masterAdmins));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listAvailableCompaniesSaga() {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.getRequest(COMPANY.LIST_AVAILABLE_COMPANIES)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeAvailableCompanies(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getCompanyMembershipsSaga(action) {
  yield put(hideError());
  try {
    const url = action.year
      ? `${COMPANY.GET_COMPANY_MEMBERSHIPS}?year=${action.year}`
      : COMPANY.GET_COMPANY_MEMBERSHIPS;
    const response = yield call(() => AuthUtil.getRequest(url));
    if (response.data.success) {
      yield put(storeCompanyMemberships(response.data.memberships || []));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listActiveLicenseSaga() {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.getRequest(COMPANY.LIST_ACTIVE_LICENSE)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeActiveLicense(response.data.licenses));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* createCompanySaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.CREATE_COMPANY, action.company)
    );
    if (response.data.success) {
      yield put(storeCompanies(response.data.companies));
      action.refresh();
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* updateCompanySaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.UPDATE_COMPANY, action.updatedCompany)
    );
    if (response.data.success) {
      yield put(storeCompanies(response.data.companies));
      // Refresh available companies so the company picker updates
      // without requiring a re-login (linked locations may have changed).
      yield call(listAvailableCompaniesSaga);

      // Sync userInfo.selectedLocations from the updated company data
      // so the company picker visibility updates immediately.
      const userInfo = yield select(userInfoSelector);
      if (userInfo) {
        const myCompany = (response.data.companies || []).find(
          (c) => c.companyId === userInfo.companyId
        );
        if (myCompany) {
          const auth = userInfo.authority || [];
          const isOwnerOrAdmin =
            auth.includes('ROLE_OWNER') || auth.includes('ROLE_MASTER_ADMIN');
          if (isOwnerOrAdmin && myCompany.linkedCompanies) {
            yield put(
              updateUserInfo({
                ...userInfo,
                selectedLocations: myCompany.linkedCompanies,
              })
            );
          }
        }
      }

      if (action.refresh) action.refresh();
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* generateLicenseSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.GENERATE_LICENSE, action.data)
    );
    if (response.data.success) {
      yield put(storeLicenseKey(response.data.licenseKey));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* revokeLicenseSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.REVOKE_LICENSE, action.data)
    );
    if (response.data.success) {
      yield put(storeLicenseKey(response.data.licenseKey));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* renewLicenseSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.RENEW_LICENSE, action.data)
    );

    if (response.data.success) {
      yield put(storeLicenseKey(response.data.licenseKey));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, response.data.message);
      }
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
  }
}

function* updateLicenseAmountSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.UPDATE_LICENSE_AMOUNT, action.data)
    );

    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, response.data.licenseKey);
      }
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
  }
}

function* actualProjectionSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.ACTUAL_PROJECTION, action.data)
    );

    if (response.data.success) {
      yield put(storeActualProjections(response.data.list));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* yearlyProjectionSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.YEARLY_PROJECTION, action.data)
    );

    if (response.data.success) {
      yield put(storeYearlyProjections(response.data.list));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getLicenseSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.GET_LICENSE, action.data)
    );
    if (response.data.success) {
      yield put(storeLicenseKey(response.data.licenseKey));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* updateLicensePaymentSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.UPDATE_LICENSE_PAYMENT, action.data)
    );
    if (response.data.success) {
      // Refresh the list so the UI picks up both the edited row and
      // any lastModifiedBy stamps the backend just wrote. We need the
      // companyId off the action payload (same one the dialog sent on
      // open) to scope the refetch correctly.
      if (action.data && action.data.companyId) {
        const refreshed = yield call(() =>
          AuthUtil.postRequest(COMPANY.LIST_LICENSE_PAYMENTS, {
            companyId: action.data.companyId,
          })
        );
        if (refreshed.data.success) {
          yield put(
            storeLicensePayments(
              refreshed.data.payments || [],
              refreshed.data.totalCount || 0
            )
          );
        }
      }
      if (action.callback && typeof action.callback === 'function') {
        // Pass reactivation signal through so the dialog can render the
        // "Company reactivated" banner when Mark Paid brought a revoked
        // company back online (Phase 1 Case B).
        action.callback(true, response.data.message, {
          reactivated: !!response.data.reactivated,
          reactivationMessage: response.data.reactivationMessage,
          payment: response.data.payment,
        });
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
      if (response.data.message) {
        yield put(showErrorMessage(response.data.message));
      }
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* markCyclePaidSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.MARK_CYCLE_PAID, action.data)
    );
    if (response.data.success) {
      // Refresh the membership summary so the row's status & paid totals
      // re-derive from the new collected row. The summary always loads by
      // year, so reuse the same year the action was scoped to.
      const refreshYear = action.data && action.data.year;
      if (refreshYear) {
        const url = `${COMPANY.GET_COMPANY_MEMBERSHIPS}?year=${refreshYear}`;
        const refreshed = yield call(() => AuthUtil.getRequest(url));
        if (refreshed.data.success) {
          yield put(storeCompanyMemberships(refreshed.data.memberships || []));
        }
      }
      if (action.callback && typeof action.callback === 'function') {
        action.callback(true, response.data.message, {
          reactivated: !!response.data.reactivated,
          reactivationMessage: response.data.reactivationMessage,
        });
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
      if (response.data.message) {
        yield put(showErrorMessage(response.data.message));
      }
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* recordNextCyclePaymentSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.RECORD_NEXT_CYCLE_PAYMENT, action.data)
    );
    if (response.data.success) {
      // Refresh the list so the dialog picks up the new pre-cycle row,
      // along with any audit / reactivation side-effects the server
      // applied (e.g. payment.licenseKey stamped after auto-reactivate).
      if (action.data && action.data.companyId) {
        const refreshed = yield call(() =>
          AuthUtil.postRequest(COMPANY.LIST_LICENSE_PAYMENTS, {
            companyId: action.data.companyId,
          })
        );
        if (refreshed.data.success) {
          yield put(
            storeLicensePayments(
              refreshed.data.payments || [],
              refreshed.data.totalCount || 0
            )
          );
        }
      }
      if (action.callback && typeof action.callback === 'function') {
        action.callback(true, response.data.message, {
          reactivated: !!response.data.reactivated,
          reactivationMessage: response.data.reactivationMessage,
          payment: response.data.payment,
        });
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message, {
          existingPaymentId: response.data.existingPaymentId,
        });
      }
      if (response.data.message) {
        yield put(showErrorMessage(response.data.message));
      }
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listLicensePaymentsSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.LIST_LICENSE_PAYMENTS, action.data)
    );
    if (response.data.success) {
      yield put(
        storeLicensePayments(
          response.data.payments || [],
          response.data.totalCount || 0
        )
      );
    } else {
      yield put(storeLicensePayments([], 0));
      if (response.data.message) {
        yield put(showErrorMessage(response.data.message));
      }
    }
  } catch (error) {
    yield put(storeLicensePayments([], 0));
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* activateLicenseSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(COMPANY.ACTIVATE_LICENSE, action.data)
    );
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      yield put(showErrorMessage(response.data.message));
      // Always invoke the callback so callers like LockedShell can clear
      // their "Activating…" loading state. Existing Login dialog callers
      // gate on `success === true`, so they're unaffected by the false
      // call. New callers must handle the failure case explicitly.
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

//Machine
function* listMachine(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.LIST_MACHINE, {
        locationId: action.locationId,
      })
    );
    const machines = response.data.machines.map((machine) => ({
      machine: machine.machine,
      machineId: machine.machineId,
      key: machine.machineId,
    }));
    yield put(storeMachine(machines));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* editMachine(action) {
  yield put(hideError());
  try {
    const request = {
      machines: action.machines,
      locationId: action.locationId,
      deletedMachineIds: action.deletedMachineIds || [],
    };
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.EDIT_MACHINE, request)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }

      const machines = response.data.machines.map((machine) => ({
        machine: machine.machine,
        machineId: machine.machineId,
        key: machine.machineId,
      }));
      yield put(storeMachine(machines));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listGame(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(GAME.LIST_GAME, { locationId: action.locationId })
    );
    const games = response.data.games.map((game) => ({
      type: game.type,
      gameId: game.gameId,
      key: game.gameId,
    }));
    yield put(storeGame(games));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* editGame(action) {
  yield put(hideError());
  try {
    const request = {
      games: action.games,
      locationId: action.locationId,
    };
    const response = yield call(() =>
      AuthUtil.postRequest(GAME.EDIT_GAME, request)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }

      const games = response.data.games.map((game) => ({
        type: game.type,
        gameId: game.gameId,
        key: game.gameId,
      }));
      yield put(storeGame(games));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listReadingSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.LIST_READING, {
        sessionId: action.sessionId,
        locationId: action.locationId,
      })
    );
    const readings = response.data.readings;
    yield put(storeAllReadings(readings));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listSessionIdsSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.LIST_SESSION_ID, {
        fromDate: action.fromDate,
        toDate: action.toDate,
        locationId: action.locationId,
      })
    );
    const sessionIds = response.data.sessionIds;
    yield put(storeAllSessionIds(sessionIds));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listAssignMachine(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.LIST_ASSIGN_MACHINE, {
        locationId: action.locationId,
      })
    );
    const assignTemplates = response.data.assignTemplates;
    yield put(storeAssignMachine(assignTemplates));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* editAssignMachine(action) {
  yield put(hideError());
  try {
    const request = {
      assignTemplates: action.assignTemplates,
      locationId: action.locationId,
      deletedAssignMachineIds: action.deletedAssignMachineIds || [],
    };
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.EDIT_ASSIGN_MACHINE, request)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }

      const data = response.data.assignTemplates;
      yield put(storeAssignMachine(data.assignTemplates));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* resetMachineSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.RESET_MACHINE, action.assignMachineId)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* updateReadingSaga(action) {
  yield put(hideError());
  try {
    yield put(startLoading());
    const request = {
      locationId: action.locationId,
      data: action.data,
    };
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.UPDATE_READING, request)
    );

    if (response.status === 200 && response.data.success) {
      const reading = response.data.reading;
      yield put(storeReading(reading));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, null);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

function* listMatchTicketsSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.LIST_MATCH_TICKETS, {
        sessionId: action.sessionId,
        locationId: action.locationId,
      })
    );
    if (response.status === 200 && response.data.success) {
      const differenceList = response.data.differenceList;
      const employeeTickets = response.data.employeeTickets;
      yield put(storeAllMatchTickets(differenceList, employeeTickets));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* clockInSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CLOCK.CLOCK_IN, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }
      yield put(storeClock(response.data.clockRecord));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* clockOutSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CLOCK.CLOCK_OUT, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }
      yield put(storeClock(response.data.clockRecord));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* loadClockRecordSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CLOCK.LOAD_CLOCK_RECORD, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }
      yield put(storeClock(response.data.clockRecord));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      //yield put(showErrorMessage(response.data.message))
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listEmployeeSessionSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CLOCK.LIST_EMPLOYEE_SESSION, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeAllEmployeeSession(response.data.employeeSession));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* updateEmployeeStatusSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CLOCK.UPDATE_EMPLOYEE_STATUS, action.data)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.data.success,
    };
    if (status.status === HTTP_SUCCESS && status.success) {
      for (let i = 0; i < response.data.clockRecord.length; i++) {
        yield put(storeUpdatedEmployeeSession(response.data.clockRecord[i]));
      }
    } else {
      status.message = response.data?.message;
      yield put(requestStatus(status));
    }
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* manualClockInSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CLOCK.MANUAL_CLOCK_IN, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }
      yield put(storeClock(response.data.clockRecord));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteEmployeeSessionSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CLOCK.DELETE_EMPLOYEE_SESSION, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeAllEmployeeSession(response.data.employeeSession));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Employee session deleted');
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* forceClockOutSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CLOCK.FORCE_CLOCK_OUT, action.data)
    );
    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Employee clocked out and logged out');
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getPointWatchingSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.GET_POINT_WATCHING, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storePointWatching(response.data.pointsWatching));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getPointWatchingByDateSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.POINT_WATCHING_BY_DATE, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(
        storePointWatchingByDate({
          date: response.data.date,
          entries: response.data.entries || [],
          employees: response.data.employees || [],
        })
      );
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getPointWatchingEmployeeDaysSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.POINT_WATCHING_EMPLOYEE_DAYS, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storePointWatchingEmployeeDays(response.data.days || []));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getPointWatchingDayEntriesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.POINT_WATCHING_DAY_ENTRIES, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storePointWatchingDayEntries(response.data.entries || []));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getPointWatchingAlertsSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.POINT_WATCHING_ALERTS, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storePointWatchingAlerts(response.data.alerts || []));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* clearPointWatchingAlertSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.CLEAR_POINT_WATCHING_ALERT, action.data)
    );
    if (response.status === 200 && response.data.success) {
      if (action.callback) action.callback(null, response.data.message);
    } else if (action.callback) {
      action.callback(response.data.message || 'Failed to clear alert');
    }
  } catch (error) {
    if (action.callback) action.callback(SERVER_FAILED_ERROR_MESSAGE);
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* matchPointReportPreviewSaga(action) {
  yield call(clearStatus);
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(DASHBOARD.MATCH_POINT_REPORT_PDF, action.pdfPreview)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    if (response.data.success) {
      yield put(
        storeMatchPointPDFPreviewReady(Uint8Array.from(response.data.pdfData))
      );
    } else {
      yield put(storeMatchPointPDFPreviewReady(new Uint8Array(0)));
      yield put(showErrorMessage(response.data.message));
    }
    yield put(requestStatus(status));
  } catch (e) {
    yield put(showErrorMessage('Failed to load PDF'));
    yield put(storeMatchPointPDFPreviewReady(new Uint8Array(0)));
  }
}

function* masterReadingReportPreviewSaga(action) {
  yield call(clearStatus);
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(
        DASHBOARD.MASTER_READING_REPORT_PDF,
        action.pdfPreview
      )
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    if (response.data.success) {
      yield put(
        storeMasterReadingPDFPreviewReady(
          Uint8Array.from(response.data.pdfData)
        )
      );
    } else {
      yield put(storeMasterReadingPDFPreviewReady(new Uint8Array(0)));
      yield put(showErrorMessage(response.data.message));
    }
    yield put(requestStatus(status));
  } catch (e) {
    yield put(showErrorMessage('Failed to load PDF'));
    yield put(storeMasterReadingPDFPreviewReady(new Uint8Array(0)));
  }
}

// manage BONUS
function* listManageBonusSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BONUS_API.LIST_MANAGE_BONUS, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeManageBonus(response.data.bonus));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveManageBonusSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BONUS_API.SAVE_MANAGE_BONUS, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }

      yield put(storeManageBonus(response.data.bonus.bonus));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteManageBonusSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BONUS_API.DELETE_MANAGE_BONUS, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeManageBonus(response.data.bonus));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

//BONUS

function* listClaimBonusSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BONUS_API.LIST_CLAIM_BONUS, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeClaimBonus(response.data.bonusList));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* previewBonusImageSaga(action) {
  yield call(clearStatus);
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BONUS_API.PREVIEW_BONUS_IMAGE, action.previewRequest)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };

    if (!response.data.success) {
      yield put(showErrorMessage(response.data.message));
    } else {
      yield put(storeUpdatedBonusImageWithPreview(response.data.previewedFile));
    }
    yield put(requestStatus(status));
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteBonusSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BONUS_API.DELETE_BONUS, action.data)
    );

    if (response.data.success) {
      yield put(
        listClaimBonus({
          userId: action.data.userId,
          locationId: action.data.locationId,
        })
      );
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* uploadBonusPhotoSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postMultipartRequest(BONUS_API.UPLOAD_BONUS_PHOTO, action.data)
    );
    if (!response.data.success) {
      yield put(showErrorMessage(response.data.message));
    }
    // Success is silent — no callback, no redux update needed
  } catch (error) {
    yield put(
      showErrorMessage(
        'Photo upload failed. The bonus was saved but photo is missing.'
      )
    );
  }
}

function* saveBonusSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(BONUS_API.SAVE_BONUS, action.data)
    );
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(true, response.data);
      }
      yield put(
        listClaimBonus({
          userId: action.data.userId,
          locationId: action.data.locationId,
        })
      );
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// manage rules
function* listManageRulesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(RULES.LIST_RULES, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeRules(response.data.rules));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveManageRulesSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(RULES.SAVE_RULES, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }

      yield put(storeRules(response.data.rules));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

//machine picture
function* addMachinePictureSaga(action) {
  yield put(hideError());
  try {
    yield put(startLoading());

    const { newPhotos = [], ...rest } = action.data || {};

    const formData = new FormData();
    formData.append('data', JSON.stringify(rest));
    newPhotos.forEach((p) => {
      if (p?.file) formData.append('photos', p.file, p.displayName);
    });

    const response = yield call(() =>
      AuthUtil.postMultipartRequest(MACHINE.ADD_MACHINE_PICTURE, formData)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

function* listMachinePictureSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.LIST_MACHINE_PICTURE, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeAllMachinePicture(response.data.allMachinePictures));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteMachinePictureSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.DELETE_MACHINE_PICTURE, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeAllMachinePicture(response.data.allMachinePictures));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listCategory(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(TRANSACTION.LIST_CATEGORY, action.locationId)
    );
    const categories = response.data.categories.map((category) => ({
      name: category.name,
      categoryId: category.categoryId,
      expenseType: category.expenseType,
      key: category.categoryId,
      isGlobal: category.isGlobal,
    }));
    yield put(storeCategory(categories));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* editCategory(action) {
  yield put(hideError());
  try {
    const request = {
      categories: action.categories,
      locationId: action.locationId,
    };
    const response = yield call(() =>
      AuthUtil.postRequest(TRANSACTION.EDIT_CATEGORY, request)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }

      const categories = response.data.categories.map((category) => ({
        name: category.name,
        categoryId: category.categoryId,
        expenseType: category.expenseType,
        key: category.categoryId,
        isGlobal: category.isGlobal,
      }));
      yield put(storeCategory(categories));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// reading session

function* availableEmployeeSessionSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.AVAILABLE_EMPLOYEE_SESSION, action.data)
    );
    yield put(storeAvailableEmployeeSessions(response.data.sessions));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* loadStartedSessionSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.LOAD_STARTED_SESSION, action.locationId)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeStartedSession(response.data));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* startSessionSaga(action) {
  yield put(hideError());
  try {
    yield put(startLoading());
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.START_SESSION, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, null);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

function* cancelSessionSaga(action) {
  yield put(hideError());
  try {
    yield put(startLoading());
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.CANCEL_SESSION, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

function* listDeletedReadingSessionsSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.LIST_DELETED_READING_SESSIONS, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.deletedSessions);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* restoreReadingSessionSaga(action) {
  yield put(hideError());
  try {
    yield put(startLoading());
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.RESTORE_READING_SESSION, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

function* deleteForeverReadingSessionSaga(action) {
  yield put(hideError());
  try {
    yield put(startLoading());
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.DELETE_FOREVER_READING_SESSION, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

function* endSessionSaga(action) {
  yield put(hideError());
  try {
    yield put(startLoading());
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.END_SESSION, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, null);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

function* getPrevInAndOutSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.GET_PREV_IN_OUT, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* openReadingSession(action) {
  yield put(hideError());
  try {
    yield put(startLoading());
    const response = yield call(() =>
      AuthUtil.postRequest(MACHINE.OPEN_LAST_READING_SESSION, action.locationId)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

function* getLastTwoReadingSession(action) {
  yield put(hideError());
  try {
    yield put(startLoading());
    const response = yield call(() =>
      AuthUtil.postRequest(
        MACHINE.GET_LAST_TWO_READING_SESSION,
        action.locationId
      )
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.sessions);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  } finally {
    yield put(stopLoading());
  }
}

//notes

function* listNotes(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(NOTE.LIST_NOTES, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeNotes(response.data.notes));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteNote(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(NOTE.DELETE_NOTE, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeNotes(response.data.notes));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* addNote(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(NOTE.ADD_NOTE, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }
      yield put(storeNotes(response.data.notes));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listReport(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(REPORT.LIST_REPORT, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(
        storeReport(
          response.data.weekReport,
          response.data.summary,
          response.data.employeeSummary,
          response.data.managerSummary,
          response.data.summaryBreakdown
        )
      );
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listShortAnalysis(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(REPORT.SHORT_ANALYSIS, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeShortAnalysis(response.data.shortAnalysis));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveManagerSetting(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(REPORT.SAVE_MANAGER_SUMMARY, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'Saved');
      }
      yield put(storeManagerSetting(response.data.data));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* saveManagerSummary(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(REPORT.UPDATE_MANAGER_SUMMARY, action.data)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getManagerSummary(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(REPORT.GET_MANAGER_SUMMARY, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeManagerSetting(response.data.data));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* updateReportField(action) {
  yield put(hideError());
  try {
    const request = {
      sessionId: action.sessionId,
      field: action.field,
      newValue: action.newValue,
      locationId: action.locationId,
    };
    const response = yield call(() =>
      AuthUtil.postRequest(REPORT.UPDATE_REPORT_FIELD, request)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(true, response.data.weekReport);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* updateManagerBankSaga(action) {
  yield put(hideError());
  try {
    const request = {
      sessionId: action.data.sessionIds,
      type: action.data.type,
      amount: action.data.amount,
      locationId: action.data.locationId,
      employeeName: action.data.employeeName,
      nameInput: action.data.nameInput,
    };
    const response = yield call(() =>
      AuthUtil.postRequest(REPORT.UPDATE_MANAGER_BANK, request)
    );

    if (response.status === 200 && response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(true, response.data.weekReport);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// maintenance permit
function* loadMaintenancePermitSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MAINTENANCE_PERMIT.CURRENT, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeMaintenancePermit(response.data.permit || null));
      yield put(storeMaintenancePermitHistory(response.data.history || []));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* grantMaintenancePermitSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MAINTENANCE_PERMIT.GRANT, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeMaintenancePermit(response.data.permit || null));
      yield put(storeMaintenancePermitHistory(response.data.history || []));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, response.data.permit);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* revokeMaintenancePermitSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(MAINTENANCE_PERMIT.REVOKE, action.data)
    );
    if (response.status === 200 && response.data.success) {
      yield put(storeMaintenancePermit(null));
      yield put(storeMaintenancePermitHistory(response.data.history || []));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, response.data.permit);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback('error', response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

export default function* settingsSagas() {
  //COMPANY

  yield takeLatest(LIST_COMPANIES, listCompaniesSaga);
  yield takeLatest(LIST_AVAILABLE_COMPANIES, listAvailableCompaniesSaga);
  yield takeLatest(CREATE_COMPANY, createCompanySaga);
  yield takeLatest(UPDATE_COMPANY, updateCompanySaga);
  yield takeLatest(LIST_MASTER_ADMINS_LOCATION, listMasterAdminPerLocationSaga);

  yield takeLatest(ACTIVATE_LICENSE, activateLicenseSaga);
  yield takeLatest(LIST_LICENSE_PAYMENTS, listLicensePaymentsSaga);
  yield takeLatest(GET_COMPANY_MEMBERSHIPS, getCompanyMembershipsSaga);
  yield takeLatest(UPDATE_LICENSE_PAYMENT, updateLicensePaymentSaga);
  yield takeLatest(RECORD_NEXT_CYCLE_PAYMENT, recordNextCyclePaymentSaga);
  yield takeLatest(MARK_CYCLE_PAID, markCyclePaidSaga);
  yield takeLatest(GENERATE_LICENSE, generateLicenseSaga);
  yield takeLatest(GET_LICENSE, getLicenseSaga);
  yield takeLatest(RENEW_LICENSE, renewLicenseSaga);
  yield takeLatest(REVOKE_LICENSE, revokeLicenseSaga);
  yield takeLatest(LIST_ACTIVE_LICENSES, listActiveLicenseSaga);
  yield takeLatest(UPDATE_LICENSE_AMOUNT, updateLicenseAmountSaga);
  yield takeLatest(YEARLY_PROJECTIONS, yearlyProjectionSaga);
  yield takeLatest(ACTUAL_PROJECTIONS, actualProjectionSaga);

  //MACHINE
  yield takeLatest(EDIT_MACHINE, editMachine);
  yield takeLatest(LIST_MACHINE, listMachine);
  yield takeLatest(EDIT_GAME, editGame);
  yield takeLatest(LIST_GAME, listGame);
  //ASSIGN MACHINE
  yield takeLatest(EDIT_ASSIGN_MACHINE, editAssignMachine);
  yield takeLatest(LIST_ASSIGN_MACHINE, listAssignMachine);
  yield takeLatest(UPDATE_READING, updateReadingSaga);
  yield takeLatest(LIST_READING, listReadingSaga);
  yield takeLatest(LIST_SESSION_IDS, listSessionIdsSaga);
  yield takeLatest(RESET_ASSIGN_MACHINE, resetMachineSaga);
  //match tickets
  yield takeLatest(LIST_MATCH_TICKETS, listMatchTicketsSaga);
  //CLOCK
  yield takeLatest(CLOCK_IN, clockInSaga);
  yield takeLatest(CLOCK_OUT, clockOutSaga);
  yield takeLatest(LOAD_CLOCK_RECORD, loadClockRecordSaga);
  //EMPLOYEE SESSION
  yield takeLatest(LIST_EMPLOYEE_SESSION, listEmployeeSessionSaga);
  yield takeLatest(UPDATE_EMPLOYEE_STATUS, updateEmployeeStatusSaga);
  yield takeLatest(MANUAL_CLOCK_IN, manualClockInSaga);
  yield takeLatest(DELETE_EMPLOYEE_SESSION, deleteEmployeeSessionSaga);
  yield takeLatest(FORCE_CLOCK_OUT, forceClockOutSaga);
  //POINTS WATCHING REPORT
  yield takeLatest(GET_POINT_WATCHING, getPointWatchingSaga);
  yield takeLatest(GET_POINT_WATCHING_BY_DATE, getPointWatchingByDateSaga);
  yield takeLatest(GET_POINT_WATCHING_EMPLOYEE_DAYS, getPointWatchingEmployeeDaysSaga);
  yield takeLatest(GET_POINT_WATCHING_DAY_ENTRIES, getPointWatchingDayEntriesSaga);
  yield takeLatest(GET_POINT_WATCHING_ALERTS, getPointWatchingAlertsSaga);
  yield takeLatest(CLEAR_POINT_WATCHING_ALERT, clearPointWatchingAlertSaga);
  yield takeLatest(MATCH_POINT_PDF_PREVIEW, matchPointReportPreviewSaga);
  //MASTER READING REPORT
  yield takeLatest(MASTER_READING_PDF_PREVIEW, masterReadingReportPreviewSaga);

  // MANAGE BONUS
  yield takeLatest(LIST_MANAGE_BONUS, listManageBonusSaga);
  yield takeLatest(SAVE_MANAGE_BONUS, saveManageBonusSaga);
  yield takeLatest(DELETE_MANAGE_BONUS, deleteManageBonusSaga);

  //Bonus
  yield takeLatest(DELETE_BONUS, deleteBonusSaga);
  yield takeLatest(SAVE_BONUS, saveBonusSaga);
  yield takeLatest(UPLOAD_BONUS_PHOTO, uploadBonusPhotoSaga);
  yield takeLatest(LIST_CLAIM_BONUS, listClaimBonusSaga);
  yield takeLatest(PREVIEW_BONUS_IMAGE, previewBonusImageSaga);

  // MANAGE RULES
  yield takeLatest(LIST_RULES, listManageRulesSaga);
  yield takeLatest(SAVE_RULES, saveManageRulesSaga);

  //Machine picture
  yield takeLatest(ADD_MACHINE_PICTURE, addMachinePictureSaga);
  yield takeLatest(LIST_MACHINE_PICTURE, listMachinePictureSaga);
  yield takeLatest(DELETE_MACHINE_PICTURE, deleteMachinePictureSaga);

  //transaction category
  yield takeLatest(EDIT_CATEGORY, editCategory);
  yield takeLatest(LIST_CATEGORY, listCategory);

  //reading session
  yield takeLatest(AVAILABLE_EMPLOYEE_SESSIONS, availableEmployeeSessionSaga);
  yield takeLatest(START_SESSION, startSessionSaga);
  yield takeLatest(END_SESSION, endSessionSaga);
  yield takeLatest(CANCEL_SESSION, cancelSessionSaga);
  yield takeLatest(
    LIST_DELETED_READING_SESSIONS,
    listDeletedReadingSessionsSaga
  );
  yield takeLatest(RESTORE_READING_SESSION, restoreReadingSessionSaga);
  yield takeLatest(
    DELETE_FOREVER_READING_SESSION,
    deleteForeverReadingSessionSaga
  );
  yield takeLatest(LOAD_STARTED_SESSION, loadStartedSessionSaga);
  yield takeLatest(GET_PREV_IN_OUT, getPrevInAndOutSaga);
  yield takeLatest(OPEN_SESSION_READING, openReadingSession);
  yield takeLatest(GET_LAST_SESSION_READING, getLastTwoReadingSession);

  //Notes
  yield takeLatest(ADD_NOTE, addNote);
  yield takeLatest(LIST_NOTES, listNotes);
  yield takeLatest(DELETE_NOTE, deleteNote);

  //week report
  yield takeLatest(LIST_REPORT, listReport);
  yield takeLatest(LIST_SHORT_ANALYSIS, listShortAnalysis);
  yield takeLatest(UPDATE_REPORT_FIELD, updateReportField);
  yield takeLatest(UPDATE_MANAGER_BANK, updateManagerBankSaga);
  yield takeLatest(GET_MANAGER_SETTING, getManagerSummary);
  yield takeLatest(SAVE_MANAGER_SETTING, saveManagerSetting);
  yield takeLatest(SAVE_MANAGER_SUMMARY, saveManagerSummary);

  //maintenance permit
  yield takeLatest(LOAD_MAINTENANCE_PERMIT, loadMaintenancePermitSaga);
  yield takeLatest(GRANT_MAINTENANCE_PERMIT, grantMaintenancePermitSaga);
  yield takeLatest(REVOKE_MAINTENANCE_PERMIT, revokeMaintenancePermitSaga);
}
